import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CameraPermissionState } from '@/shared/camera/camera-permissions';
import {
  CAMERA_PERMISSION_STATES,
  readCameraPermissionState,
  requestEnvironmentCameraStream,
  stopMediaStream,
} from '@/shared/camera/camera-permissions';
import { sortValueScanCandidates, type ValueScanCandidate, type ValueScanPurpose } from '@/shared/camera/value-scanner';
import {
  attachStreamToVideoElement,
  CompositeScannerAdapter,
  scanBarcodeFrame,
  waitForVideoElement,
} from '@/shared/camera/value-scanner-camera';

interface ValueScannerState {
  candidates: ValueScanCandidate[];
  error: string | null;
  isActive: boolean;
  isBarcodeSupported: boolean;
  lastCandidate: ValueScanCandidate | null;
  permissionState: CameraPermissionState;
}

interface UseValueScannerInput {
  autoAcceptHighConfidence?: boolean;
  autoStart?: boolean;
  onAccept: (candidate: ValueScanCandidate) => Promise<void> | void;
  purpose: ValueScanPurpose;
}

export function useValueScanner({
  autoAcceptHighConfidence = false,
  autoStart = false,
  onAccept,
  purpose,
}: UseValueScannerInput) {
  const scannerAdapter = useMemo(() => new CompositeScannerAdapter(), []);
  const acceptedCandidateIdsRef = useRef(new Set<string>());
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const isStartingRef = useRef(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [state, setState] = useState<ValueScannerState>({
    candidates: [],
    error: null,
    isActive: false,
    isBarcodeSupported: false,
    lastCandidate: null,
    permissionState: CAMERA_PERMISSION_STATES.unknown,
  });

  const addCandidates = useCallback(
    async (candidates: ValueScanCandidate[]) => {
      const validCandidates = candidates.filter(candidate => candidate.value);

      if (validCandidates.length === 0) {
        return 0;
      }

      setState(current => {
        const candidateMap = new Map(current.candidates.map(candidate => [candidate.id, candidate]));

        for (const candidate of validCandidates) {
          candidateMap.set(candidate.id, candidate);
        }

        return {
          ...current,
          candidates: sortValueScanCandidates([...candidateMap.values()]),
          lastCandidate: validCandidates[0] ?? current.lastCandidate,
        };
      });

      const [bestCandidate] = sortValueScanCandidates(validCandidates);

      if (!bestCandidate) {
        return 0;
      }

      if (
        autoAcceptHighConfidence &&
        bestCandidate.confidence >= 0.95 &&
        !acceptedCandidateIdsRef.current.has(bestCandidate.id)
      ) {
        acceptedCandidateIdsRef.current.add(bestCandidate.id);
        await onAccept(bestCandidate);
      }

      return validCandidates.length;
    },
    [autoAcceptHighConfidence, onAccept],
  );

  useEffect(() => {
    let isMounted = true;

    async function loadSupportState() {
      const [isBarcodeSupported, permissionState] = await Promise.all([
        scannerAdapter.isSupported(),
        readCameraPermissionState(),
      ]);

      if (isMounted) {
        setState(current => ({ ...current, isBarcodeSupported, permissionState }));
      }
    }

    void loadSupportState();

    return () => {
      isMounted = false;
      stopMediaStream(streamRef.current);
    };
  }, [scannerAdapter]);

  const stop = useCallback(() => {
    stopMediaStream(streamRef.current);
    streamRef.current = null;

    setState(current => ({ ...current, isActive: false }));
  }, []);

  const start = useCallback(async () => {
    if (isStartingRef.current) {
      return;
    }

    isStartingRef.current = true;
    setState(current => ({ ...current, error: null }));

    let stream: MediaStream | null = null;

    try {
      stream = await requestEnvironmentCameraStream();
      streamRef.current = stream;
      setState(current => ({ ...current, isActive: true, permissionState: CAMERA_PERMISSION_STATES.granted }));

      const video = await waitForVideoElement(() => videoRef.current);
      await attachStreamToVideoElement(video, stream);

      setState(current => ({ ...current, isActive: true, permissionState: CAMERA_PERMISSION_STATES.granted }));
    } catch (error) {
      stopMediaStream(stream);
      streamRef.current = null;
      setState(current => ({
        ...current,
        error: error instanceof Error ? error.message : 'Unable to start camera scanner.',
        isActive: false,
        permissionState: CAMERA_PERMISSION_STATES.denied,
      }));
    } finally {
      isStartingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (
      !(
        autoStart &&
        !state.isActive &&
        state.permissionState !== CAMERA_PERMISSION_STATES.denied &&
        state.permissionState !== CAMERA_PERMISSION_STATES.unsupported
      )
    ) {
      return;
    }

    void start();
  }, [autoStart, start, state.isActive, state.permissionState]);

  useEffect(() => {
    if (!(state.isActive && state.isBarcodeSupported)) {
      return;
    }

    const interval = window.setInterval(() => {
      void scanBarcodeFrame({
        adapter: scannerAdapter,
        canvas: canvasRef.current,
        onCandidates: addCandidates,
        purpose,
        video: videoRef.current,
      }).catch(() => undefined);
    }, 700);

    return () => window.clearInterval(interval);
  }, [addCandidates, purpose, scannerAdapter, state.isActive, state.isBarcodeSupported]);

  return {
    ...state,
    canvasRef,
    start,
    stop,
    videoRef,
  };
}
